import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import moment from "moment";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { FormValidationService } from "src/app/core/services/form-validation.service";
import { QuoteViewerService } from "src/app/core/services/quote-viewer.service";
import { OOVenueService } from "src/app/core/services/venue-service";
import { SalesforcePaymentProfile } from "../../models/DTO/quote-viewer.dto";
import { PaymentProfile } from "../../models/DTO/VenueFinance.dto";
import { AppMessageSeverity } from "../../models/enums/AppMessageSeverity.enum";

@Component({
	selector: "app-payment-profile-setup",
	templateUrl: "./payment-profile-setup.component.html",
	styleUrls: ["../../../modules/quote-viewer/quote-viewer.scss"],
})
export class PaymentProfileSetupComponent implements OnInit {
	@Input() source: string;
	@Output() sourceChange = new EventEmitter<string>();

	@Input() venueName: string;
	@Output() venueNameChange = new EventEmitter<string>();

	@Input() profileComplete: boolean;
	@Output() profileCompleteChange = new EventEmitter<boolean>();

	@Input() completedRecord: any;
	@Output() completedRecordChange = new EventEmitter<any>();

	paymentProfile: SalesforcePaymentProfile = new SalesforcePaymentProfile();

	profileCompletedBy = {
		firstName: undefined,
		lastName: undefined,
		title: undefined,
		email: undefined,
		dateTime: new Date(),
		viewerId: undefined,
		ipAddress: undefined,
	};

	billingContactFirstNameControl: UntypedFormControl;
	billingContactLastNameControl: UntypedFormControl;
	billingContactEmailControl: UntypedFormControl;
	billingContactPhoneControl: UntypedFormControl;
	bankingRoutingNumberControl: UntypedFormControl;
	bankingAccountNumberControl: UntypedFormControl;
	bankingBankNameControl: UntypedFormControl;

	constructor(
		public app: AppService,
		public quoteService: QuoteViewerService,
		public venueService: OOVenueService,
		public focuslink: FocuslinkService,
		private validatorService: FormValidationService
	) {}

	ngOnInit(): void {
		switch (this.source) {
			case "quote": {
				this.paymentProfile.fw1__Account__c = this.quoteService.quote.value.Opportunity.Account.Id;
				this.paymentProfile.fw1__First_Name__c = this.quoteService.quoteAcceptance.value.firstName;
				this.paymentProfile.fw1__Last_Name__c = this.quoteService.quoteAcceptance.value.lastName;
				this.paymentProfile.fw1__Email__c = this.quoteService.quoteAcceptance.value.emailAddress;
				this.paymentProfile.fw1__Phone__c = this.quoteService.quoteAcceptance.value.phoneNumber;
				break;
			}
		}

		this.billingContactEmailControl = new UntypedFormControl({ value: this.paymentProfile.fw1__Email__c }, [Validators.required, Validators.email]);
		this.billingContactFirstNameControl = new UntypedFormControl({ value: this.paymentProfile.fw1__First_Name__c }, [Validators.required]);
		this.billingContactLastNameControl = new UntypedFormControl({ value: this.paymentProfile.fw1__Last_Name__c }, [Validators.required]);
		this.billingContactPhoneControl = new UntypedFormControl({ value: this.paymentProfile.fw1__Phone__c }, [
			Validators.required,
			this.validatorService.phoneValueValidate(),
		]);
		this.bankingRoutingNumberControl = new UntypedFormControl({ value: this.paymentProfile.fw1__Bank_ABA_Code__c }, [
			Validators.required,
			this.validatorService.numericValueValidate(),
		]);
		this.bankingAccountNumberControl = new UntypedFormControl({ value: this.paymentProfile.fw1__Bank_Account_Number__c }, [
			Validators.required,
			this.validatorService.numericValueValidate(),
			Validators.maxLength(17),
			Validators.minLength(7),
		]);

		this.bankingBankNameControl = new UntypedFormControl({ value: this.paymentProfile.Bank_Name__c }, [Validators.required]);
	}

	printErrors(error) {
		console.log(error);
	}

	async submit() {
		const resp = await this.focuslink.getIpAddress();
		this.profileCompletedBy.ipAddress = resp.ip;
		this.profileCompletedBy.dateTime = new Date();

		this.paymentProfile.Payment_Notes__c = `Profile completed by ${this.profileCompletedBy.firstName} ${this.profileCompletedBy.lastName} (${
			this.profileCompletedBy.title
		}) via FocusLink at ${this.app.moment(this.profileCompletedBy.dateTime).format("l LT")}. IP address ${this.profileCompletedBy.ipAddress}.`;

		if (this.source === "quote") {
			this.profileCompletedBy.viewerId = this.quoteService.viewerId.value;
			this.paymentProfile.Payment_Notes__c += ` Quote Viewer ID ${this.profileCompletedBy.viewerId}`;
		}

		const venueId = this.source === "quote" ? this.quoteService.quote.value.Opportunity.Account.Id : this.venueService.venueKey.toString();
		await this.focuslink.contractFinancePostPaymentProfile(venueId, this.paymentProfile);
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Payment profile submitted successfully.` });
		this.profileComplete = true;
		this.profileCompleteChange.emit(true);
		this.completedRecordChange.emit(this.paymentProfileToFinanceModel(this.paymentProfile));
	}

	paymentProfileToFinanceModel(profile: SalesforcePaymentProfile): PaymentProfile {
		return new PaymentProfile({
			bankAccountNumber: profile.fw1__Bank_Account_Number__c
				.substring(profile.fw1__Bank_Account_Number__c.length - 4, profile.fw1__Bank_Account_Number__c.length)
				.padStart(profile.fw1__Bank_Account_Number__c.length, "X"),
			bankName: profile.Bank_Name__c,
			bankRoutingNumber: profile.fw1__Bank_ABA_Code__c,
			billingCity: profile.fw1__Billing_City__c,
			billingContactEmail: profile.fw1__Email__c,
			billingContactFirstName: profile.fw1__First_Name__c,
			billingContactLastName: profile.fw1__Last_Name__c,
			billingPostalCode: profile.fw1__Billing_Zip__c,
			billingState: profile.fw1__Billing_State__c,
			billingStreet: profile.fw1__Billing_Street__c,
			createdDate: moment().toDate(),
			creditCardNumber: profile.fw1__CreditCardNumber__c,
			creditCardExpirationDate: profile.fw1__Credit_Card_Expiration_Date__c,
			creditCardType: profile.fw1__CreditCardType__c,
			profilePaymentType: profile.fw1__Bank_Account_Number__c ? "eCheck" : "Credit Card",
		});
	}
}
